import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  #desktopQuery = window.matchMedia('(min-width: 990px)');

  get #isInlineDesktop() {
    return this.classList.contains('announcement-bar__slider--inline-desktop') && this.#desktopQuery.matches;
  }

  /**
   * SAYM: side-by-side messages are all visible, so expose them all to assistive tech;
   * switch back to rotation when the viewport shrinks.
   */
  #syncLayout = () => {
    if (this.#isInlineDesktop) {
      this.suspend();
      this.refs.slides?.forEach((slide) => slide.setAttribute('aria-hidden', 'false'));
    } else {
      this.current = this.current;
      this.resume();
    }
  };

  connectedCallback() {
    super.connectedCallback();

    this.#desktopQuery.addEventListener('change', this.#syncLayout);
    if (this.#isInlineDesktop) this.#syncLayout();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.play();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#desktopQuery.removeEventListener('change', this.#syncLayout);
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;
    // SAYM: messages are shown side by side on desktop, so there is nothing to rotate
    if (this.#isInlineDesktop) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    // SAYM: visitors who prefer reduced motion get no automatic rotation, unless the
    // merchant opted in. Then the messages still change, but swap without the
    // cross-fade (see assets/saym.css), so nothing actually moves on screen.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches && !this.hasAttribute('data-rotate-reduced')) {
      return false;
    }

    return Boolean(this.autoplayInterval);
  }

  /**
   * SAYM: hides the announcement bar for the rest of the session.
   */
  dismiss() {
    const section = this.closest('.shopify-section');
    const key = this.dataset.dismissKey;

    this.suspend();
    section?.setAttribute('hidden', '');

    if (!key) return;

    try {
      sessionStorage.setItem('saym-announcement-dismissed', key);
    } catch (error) {
      // Storage can be unavailable (private mode); hiding still works for this page view.
    }
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  // SAYM: suspend() instead of pause(); pause() sets `paused`, which made resume() bail out forever
  #handleVisibilityChange = () => (document.hidden ? this.suspend() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
